import { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import type { IStatsAggregates } from '../../../models/trainingStatistics';
import { trainingStatsEndpoints } from '../../../endpoints/trainingStatistics';
import styles from './ObjectiveDrillDown.module.scss';

/**
 * Modal drill-down per obiettivo tecnico/tattico.
 * Spec v1.1.1 sez 21.5 (righe 2864-2894):
 * - Distribuzione per metodo
 * - Distribuzione per fase
 * - Trend mensile
 * - Insight automatici
 */
interface Props {
  objectiveId: string;
  kind: 'technical' | 'tactical';
  aggregates: IStatsAggregates;
  teamId?: string;
  periodStart?: string;
  periodEnd?: string;
  onClose: () => void;
}

const METHOD_LABELS: Record<string, string> = {
  analytical: 'Analitico',
  global: 'Globale',
  situational: 'Situazionale',
  themed_game: 'Partita a tema',
  free_game: 'Partita libera',
};

export const ObjectiveDrillDownModal: FC<Props> = ({
  objectiveId,
  kind,
  aggregates,
  teamId,
  periodStart,
  periodEnd,
  onClose,
}) => {
  const { data: correlation } = useQuery({
    queryKey: ['correlation', teamId, periodStart, periodEnd],
    queryFn: () =>
      trainingStatsEndpoints.getCorrelation(
        teamId ?? '',
        periodStart ?? '',
        periodEnd ?? '',
      ),
    enabled: !!teamId && !!periodStart && !!periodEnd,
    staleTime: 10 * 60_000,
  });

  const objCorrelation = correlation?.correlations.find(
    (c) => c.objectiveId === objectiveId,
  );
  const list =
    kind === 'technical'
      ? aggregates.byTechnicalObjective ?? []
      : aggregates.byTacticalObjective ?? [];

  const obj: any = (list as any[]).find(
    (o) => o.objectiveId === objectiveId,
  );

  if (!obj) {
    return (
      <div className={styles.backdrop} onClick={onClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.header}>
            <div className={styles.title}>Obiettivo non trovato</div>
            <button type="button" className={styles.closeBtn} onClick={onClose}>
              <X size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  const byMethod = obj.byMethod ?? {};
  const methodEntries = Object.entries(byMethod).filter(
    ([, v]) => (v as number) > 0,
  );
  const totalMethod = methodEntries.reduce(
    (sum, [, v]) => sum + (v as number),
    0,
  );

  const insights = generateInsights(byMethod, totalMethod);

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <div className={styles.title}>{obj.objectiveId}</div>
            <div className={styles.subtitle}>
              Totale allenato: <strong>{obj.minutes} min</strong> (
              {obj.percentage?.toFixed(1)}%)
            </div>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className={styles.body}>
          {totalMethod > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Distribuzione per metodo</div>
              {methodEntries.map(([method, minutes]) => {
                const pct =
                  totalMethod > 0
                    ? ((minutes as number) / totalMethod) * 100
                    : 0;
                return (
                  <div key={method} className={styles.barRow}>
                    <span className={styles.barLabel}>
                      {METHOD_LABELS[method] ?? method}
                    </span>
                    <div className={styles.barTrack}>
                      <div
                        className={styles.barFill}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className={styles.barValue}>
                      {minutes as number} min
                    </span>
                    <span className={styles.barPct}>({pct.toFixed(0)}%)</span>
                  </div>
                );
              })}
            </div>
          )}

          {insights.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Insight automatici</div>
              <ul className={styles.insights}>
                {insights.map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
              </ul>
            </div>
          )}

          {objCorrelation && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                Correlazione con prestazioni
              </div>
              <div className={styles.correlationBox}>
                <div>
                  <strong>{objCorrelation.matchesCount}</strong> partite nel
                  periodo · outcome più frequente:{' '}
                  <strong>
                    {objCorrelation.avgOutcome === 'W'
                      ? 'Vittoria'
                      : objCorrelation.avgOutcome === 'D'
                        ? 'Pareggio'
                        : objCorrelation.avgOutcome === 'L'
                          ? 'Sconfitta'
                          : '—'}
                  </strong>
                </div>
                {Object.entries(objCorrelation.relatedStats).length > 0 && (
                  <div className={styles.statGrid}>
                    {Object.entries(objCorrelation.relatedStats).map(
                      ([k, v]) => (
                        <div key={k} className={styles.statCell}>
                          <span className={styles.statLabel}>{k}</span>
                          <span className={styles.statValue}>
                            {typeof v === 'number' ? v.toFixed(1) : String(v)}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Trend mensile (derivato dal nome del periodo) */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              Trend periodo corrente
            </div>
            <div className={styles.trendNote}>
              {periodStart && periodEnd ? (
                <>
                  Minuti cumulati in{' '}
                  {dayjs(periodEnd).diff(dayjs(periodStart), 'day')} giorni:{' '}
                  <strong>{obj.minutes} min</strong> (
                  {(obj.minutes / Math.max(1, dayjs(periodEnd).diff(dayjs(periodStart), 'week'))).toFixed(0)}{' '}
                  min/settimana media)
                </>
              ) : (
                <>Serie temporale non disponibile (periodo non specificato)</>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function generateInsights(
  byMethod: Record<string, number>,
  total: number,
): string[] {
  if (total === 0) return [];
  const out: string[] = [];

  const max = Math.max(...Object.values(byMethod));
  const maxKey = Object.keys(byMethod).find((k) => byMethod[k] === max) ?? '';
  if (max / total > 0.5) {
    out.push(
      `${METHOD_LABELS[maxKey] ?? maxKey} domina (>${Math.round((max / total) * 100)}%): considera più varietà.`,
    );
  } else {
    out.push(`Bilanciamento: nessun metodo domina oltre il 50%.`);
  }

  if ((byMethod.free_game ?? 0) === 0) {
    out.push(
      'Partita libera assente: la letteratura raccomanda almeno 5% per transfer in gara.',
    );
  }

  return out;
}
